// 退货收货异常标签：贴在原包装外面，提醒"这个包裹核对不一致、已暂存、等客户确认"。
// 4x6 英寸，在单独的新窗口里渲染并打印：打印对话框属于那个新窗口，不会卡住入库扫描页面本身。
// 必须在用户点击按钮的同一个事件处理里调用（否则会被浏览器的弹窗拦截器拦掉）。

export interface ReceivingExceptionLabelInfo {
  forecastCode: string;
  trackingNumber: string;
  projectLabel: string;
  reasonLabels: string[];
  /** ISO 时间，用于标签上的日期 */
  createdAt: string;
}

const escapeHtml = (value: string) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function buildReceivingExceptionLabelHtml(info: ReceivingExceptionLabelInfo, autoPrint = false): string {
  const rows: Array<[string, string]> = [
    ['预报编号', info.forecastCode],
    ['物流单号', info.trackingNumber],
    ['项目', info.projectLabel],
    ['异常原因', info.reasonLabels.join('、')],
    ['日期', formatDate(info.createdAt)],
  ];
  const body = rows
    .map(([label, value]) => `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`)
    .join('');
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>收货异常标签</title>
<style>
  @page { size: 4in 6in; margin: 0.15in; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, "Microsoft YaHei", Arial, sans-serif; color: #000; }
  .box { border: 4px solid #000; padding: 12px; height: 5.65in; }
  .title { font-size: 30px; font-weight: 800; text-align: center; border-bottom: 3px solid #000; padding-bottom: 8px; }
  .sub { font-size: 20px; font-weight: 700; text-align: center; margin: 10px 0; background: #000; color: #fff; padding: 6px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 18px; }
  th { text-align: left; width: 34%; padding: 8px 4px; vertical-align: top; border-bottom: 1px solid #999; }
  td { padding: 8px 4px; font-weight: 700; word-break: break-all; border-bottom: 1px solid #999; }
  .note { margin-top: 14px; font-size: 18px; font-weight: 700; text-align: center; }
</style></head>
<body><div class="box">
  <div class="title">收货异常</div>
  <div class="sub">暂存 · 等待客户确认</div>
  <table>${body}</table>
  <div class="note">请勿拆封 · 请勿上架</div>
</div>${autoPrint ? '<script>window.addEventListener("load", function () { setTimeout(function () { window.print(); }, 300); });</script>' : ''}</body></html>`;
}

export function printReceivingExceptionLabel(info: ReceivingExceptionLabelInfo): void {
  const win = window.open('', '_blank', 'width=520,height=760');
  if (!win) {
    throw new Error('浏览器拦截了弹出窗口，请允许本站弹出窗口后再点"打印标签"');
  }
  win.document.open();
  win.document.write(buildReceivingExceptionLabelHtml(info, true));
  win.document.close();
}
